import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { _className, ComponentSync, EditableDBItemV3, LL_H_C, LL_V_L, openContent } from '@nu-art/thunderstorm-frontend/index';
import { TS_InputV2 } from '@nu-art/thunderstorm-frontend/components/TS_V2_Input/TS_InputV2';
import './Page_UnresolvedCodes.scss';
import { sortArray } from '@nu-art/ts-common';
import { ModuleFE_FHIRQuery } from '@app/fhir-frontend';
import { ModuleFE_UnresolvedCode } from '../../_entity/unresolved-code/ModuleFE_UnresolvedCode.js';
import { EM_RefSystems, UnresolvedCodeStatus_NewAndWaitingForNLP, UnresolvedCodeStatus_WaitingForUserDecision, UnresolvedCodeStatuses } from '@app/runtime-tools-shared';
import { Editor_UnresolvedCode } from './Editor_UnresolvedCode.js';
import { Dialog_UnresolvedCodeFilters } from './Dialog_UnresolvedCodeFilters.js';
import { StatusLabels } from './status-labels.js';
import { ICONS } from '@app/styles/icons/icons';
import { ModuleFE_Variable } from '@app/hcs-frontend';
import { RoutePermissions } from '@nu-art/permissions-frontend';
export class Page_UnresolvedCodes extends ComponentSync {
    static Route = Object.freeze({
        key: 'unresolved-code',
        path: 'unresolved-code',
        Component: Page_UnresolvedCodes,
        modulesToAwait: [ModuleFE_UnresolvedCode, ModuleFE_Variable, ModuleFE_FHIRQuery],
        enabled: RoutePermissions(ModuleFE_UnresolvedCode.permissions.Release),
    });
    //######################### Life Cycle #########################
    __onUnresolvedCodeFiltersChange = (data) => {
        this.setState({
            selectedStatuses: data.selectedStatuses,
            selectedRefSystems: data.selectedRefSystems,
            selectedFhirQueryIds: data.selectedFhirQueryIds ?? [],
            filterByHasSuggestedHv: data.filterByHasSuggestedHv ?? false,
        });
    };
    __onUnresolvedCodeChanged = () => {
        // Refresh selected item if it was updated
        if (this.state.selectedItem) {
            const updatedItem = ModuleFE_UnresolvedCode.cache.unique(this.state.selectedItem._id);
            if (updatedItem) {
                // Preserve current edit mode when refreshing item
                const editable = new EditableDBItemV3(updatedItem, ModuleFE_UnresolvedCode);
                this.setState({ selectedItem: updatedItem, editable });
            }
            else {
                // Item was deleted
                this.setState({ selectedItem: undefined, editable: undefined, editMode: false });
            }
        }
        else {
            this.forceUpdate();
        }
    };
    deriveStateFromProps(nextProps, state) {
        state.search ??= '';
        state.editMode ??= false;
        state.selectedStatuses ??= [UnresolvedCodeStatus_NewAndWaitingForNLP, UnresolvedCodeStatus_WaitingForUserDecision];
        state.selectedRefSystems ??= [...EM_RefSystems];
        state.selectedFhirQueryIds ??= [];
        state.filterByHasSuggestedHv ??= false;
        return state;
    }
    //######################### Logic #########################
    getItems = () => {
        let items = ModuleFE_UnresolvedCode.cache.allMutable();
        // Filter by selected statuses and ref systems
        items = items.filter(item => {
            return this.state.selectedStatuses.includes(item.status) &&
                this.state.selectedRefSystems.includes(item.refSystem);
        });
        // Filter by selected FHIR query IDs
        if (this.state.selectedFhirQueryIds.length > 0) {
            items = items.filter(item => item.fhirQueryId != null && this.state.selectedFhirQueryIds.includes(item.fhirQueryId));
        }
        // Filter by has suggested HCS variable
        if (this.state.filterByHasSuggestedHv) {
            items = items.filter(item => (item.suggestedHvIds?.length ?? 0) > 0);
        }
        // Sort by _created timestamp (newest first)
        items = sortArray(items, item => item.__created ?? 0, true);
        const search = this.state.search.trim().toLowerCase();
        if (!search)
            return items;
        return items.filter(item => {
            return item.code.toLowerCase().includes(search) ||
                item.text.toLowerCase().includes(search) ||
                item.display?.toLowerCase().includes(search) ||
                item.fhirQueryLabel?.toLowerCase().includes(search) ||
                item.entryId?.toLowerCase().includes(search);
        });
    };
    selectItem = (item) => {
        const editable = new EditableDBItemV3(item, ModuleFE_UnresolvedCode);
        this.setState({ selectedItem: item, editable });
    };
    deselectItem = () => {
        this.setState({ selectedItem: undefined, editable: undefined, editMode: false });
    };
    setEditMode = (editMode) => {
        this.setState({ editMode });
    };
    //######################### Render #########################
    render() {
        const items = this.getItems();
        return _jsxs(LL_H_C, { id: 'page__unresolved-code', children: [_jsxs("div", { className: 'unresolved-code__list-container', children: [this.renderFilters(items.length), this.renderItemList(items)] }), this.state.editable && this.state.selectedItem &&
                    _jsx(Editor_UnresolvedCode, { editable: this.state.editable, editMode: this.state.editMode, onEditModeChange: this.setEditMode, deselect: this.deselectItem })] });
    }
    renderFilters = (count) => {
        const { selectedStatuses, selectedRefSystems, selectedFhirQueryIds, filterByHasSuggestedHv } = this.state;
        const hasFilters = selectedStatuses.length !== UnresolvedCodeStatuses.length ||
            selectedRefSystems.length !== EM_RefSystems.length ||
            selectedFhirQueryIds.length > 0 ||
            filterByHasSuggestedHv;
        const filterButtonClassName = _className('unresolved-code__filter-button', hasFilters && 'filter-active');
        return _jsxs("div", { className: 'unresolved-code__filters', children: [_jsxs("div", { className: 'unresolved-code__filters__title-row', children: ["Unresolved Codes (", count, ")"] }), _jsxs("div", { className: 'unresolved-code__filters__search-row', children: [_jsxs("div", { className: 'unresolved-code__filters__search-bar', children: [_jsx(ICONS.advisor_v5_search.component, {}), _jsx(TS_InputV2, { type: "text", value: this.state.search, onChange: (value) => this.setState({ search: value }), placeholder: "Search...", className: 'unresolved-code__search-bar__input' })] }), _jsx(ICONS.advisor_avn_filter.component, { className: filterButtonClassName, ...openContent.popUpV2.bottom({
                                content: _jsx(Dialog_UnresolvedCodeFilters, { selectedStatuses: selectedStatuses, selectedRefSystems: selectedRefSystems, selectedFhirQueryIds: selectedFhirQueryIds, filterByHasSuggestedHv: filterByHasSuggestedHv, onFiltersChange: (data) => this.setState({
                                        selectedStatuses: data.selectedStatuses,
                                        selectedRefSystems: data.selectedRefSystems,
                                        selectedFhirQueryIds: data.selectedFhirQueryIds ?? [],
                                        filterByHasSuggestedHv: data.filterByHasSuggestedHv ?? false,
                                    }) }),
                                id: 'unresolved-code__filters_popup'
                            }) })] })] });
    };
    renderItemList = (items) => {
        return _jsx("div", { className: 'unresolved-code__item-list', children: items.length === 0
                ? _jsx("div", { className: 'unresolved-code__empty-state', children: "No unresolved codes found" })
                : items.map(this.renderListItem) });
    };
    renderListItem = (item) => {
        const selected = item._id === this.state.selectedItem?._id;
        const className = _className('unresolved-code__item-list__item', selected && 'selected');
        return _jsxs(LL_V_L, { className: className, onClick: () => this.selectItem(item), children: [_jsxs("div", { className: 'unresolved-code__item__header', children: [_jsx("span", { className: 'unresolved-code__item__code', children: item.code }), _jsx("span", { className: _className('unresolved-code__item__status', 'status', item.status), children: StatusLabels[item.status] })] }), _jsx("div", { className: 'unresolved-code__item__text', children: item.text }), item.display && _jsx("div", { className: 'unresolved-code__item__display', children: item.display }), _jsxs("div", { className: 'unresolved-code__item__meta', children: [_jsx("span", { className: 'unresolved-code__item__ref-system', children: item.refSystem.toUpperCase() }), _jsx("span", { className: 'unresolved-code__item__query-label', children: item.fhirQueryLabel })] })] }, item._id);
    };
}
//# sourceMappingURL=Page_UnresolvedCodes.js.map